import request from "@/lib/api/handler";

import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useProfileQuery } from "@/queries/user/profile";

import { QueryKeys } from "@/queries/utils";
import { Candidate } from "@/queries/candidate/candidate";
import { IReference } from "@/redux/features/candidateProfileSlice";
import { toast } from "sonner";

import { CandidateReferenceFormSchema } from "@/components/forms/candidate/add-delete-reference/schema";

export const useAddReference = (
  profileId: string,
  onSuccess?: (v: IReference) => void,
) => {
  const qc = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  const { profile } = useProfileQuery();

  const { mutate, isPending, error } = useMutation({
    mutationFn: async ({ data }: { data: CandidateReferenceFormSchema }) => {
      const token = await getAccessTokenSilently();

      return request<IReference>({
        method: "POST",
        path: `/api/candidate/${profileId || "foo"}/reference`,
        token,
        body: JSON.stringify(data),
      });
    },
    onSuccess: (data) => {
      // Update reference for candidate
      const prevCandidate = qc.getQueryData<Candidate>(
        QueryKeys.candidate(profileId),
      );

      if (prevCandidate) {
        const updatedCandidate = {
          ...prevCandidate,
        };

        updatedCandidate.references = [...prevCandidate.references, data];

        qc.setQueryData<Candidate>(
          QueryKeys.candidate(profileId),
          updatedCandidate,
        );
      }

      // Update reference
      if (profile?.id === profileId) {
        const prevSelf = qc.getQueryData<Candidate>(QueryKeys.myCandidate);
        if (prevSelf) {
          const updatedCandidate = {
            ...prevSelf,
          };

          updatedCandidate.references = [...prevSelf.references, data];

          qc.setQueryData<Candidate>(QueryKeys.myCandidate, updatedCandidate);
        }
      }

      onSuccess?.(data);
    },
    onError: () => {
      toast.error("Failed to save reference, please try again later.");
    },
  });

  return {
    addReference: mutate,
    isAddingReference: isPending,
    addReferenceError: error,
  };
};
